import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { QueryClientProvider, type QueryClient, type QueryKey } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { Button, Card, Stack, Typography } from '@org/design-system';

export const SLOW_QUERY_THRESHOLD_MS = Number(import.meta.env.VITE_SLOW_QUERY_THRESHOLD_MS ?? '2000');
const MAX_SLOW_QUERIES = 5;

export interface SlowQueryEvent {
  id: string;
  queryKey: QueryKey;
  queryKeyLabel: string;
  entity: string;
  operation: string;
  layoutType: string;
  durationMs: number;
  appId: string;
  observedAt: number;
}

interface QueryPerformanceContextValue {
  slowQueries: SlowQueryEvent[];
  retryQuery: (queryKey: QueryKey) => Promise<void>;
}

const QueryPerformanceContext = createContext<QueryPerformanceContextValue | null>(null);

function toQueryKeyLabel(queryKey: QueryKey): string {
  if (typeof queryKey === 'string') return queryKey;
  if (Array.isArray(queryKey)) {
    return queryKey
      .map(item => (typeof item === 'string' ? item : JSON.stringify(item)))
      .join(':');
  }

  return JSON.stringify(queryKey);
}

function extractEntity(queryKey: QueryKey): string {
  if (Array.isArray(queryKey) && queryKey.length > 0 && typeof queryKey[0] === 'string') {
    return queryKey[0];
  }

  return 'unknown';
}

function extractOperation(queryKey: QueryKey): string {
  if (Array.isArray(queryKey) && queryKey.length > 1 && typeof queryKey[1] === 'string') {
    return queryKey[1];
  }

  return 'query';
}

function extractLayoutType(queryKey: QueryKey): string {
  if (Array.isArray(queryKey) && queryKey.includes('tabs-layout')) {
    return 'tabs';
  }

  return 'full';
}

function emitSentryBreadcrumb(payload: SlowQueryEvent) {
  const sentry = (globalThis as { Sentry?: { addBreadcrumb?: (crumb: Record<string, unknown>) => void } }).Sentry;
  sentry?.addBreadcrumb?.({
    category: 'performance.query',
    level: 'warning',
    message: 'Slow query detected',
    data: {
      appId: payload.appId,
      queryKey: payload.queryKeyLabel,
      durationMs: payload.durationMs,
    },
  });
}

async function sendUsageTrackerEvent(payload: SlowQueryEvent) {
  try {
    await fetch('/api/analytics/track', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event: 'slow_query',
        entity: payload.entity,
        operation: payload.operation,
        durationMs: payload.durationMs,
        layoutType: payload.layoutType,
        appId: payload.appId,
      }),
    });
  } catch {
    // Best-effort telemetry only.
  }
}

interface QueryPerformanceProviderProps {
  client: QueryClient;
  appId: string;
  children: ReactNode;
}

export function QueryPerformanceProvider({ client, appId, children }: QueryPerformanceProviderProps) {
  const [slowQueries, setSlowQueries] = useState<SlowQueryEvent[]>([]);

  useEffect(() => {
    const startTimes = new Map<string, number>();
    const activeFetches = new Set<string>();

    const unsubscribe = client.getQueryCache().subscribe((event) => {
      if (!event?.query) return;

      const query = event.query;
      const queryHash = query.queryHash;
      const isFetching = query.state.fetchStatus === 'fetching';

      if (event.type === 'added') {
        startTimes.set(queryHash, performance.now());
      }

      if (isFetching) {
        if (!activeFetches.has(queryHash)) {
          activeFetches.add(queryHash);
          startTimes.set(queryHash, performance.now());
        }
        return;
      }

      if (!activeFetches.has(queryHash)) {
        return;
      }

      activeFetches.delete(queryHash);
      const startedAt = startTimes.get(queryHash);
      if (startedAt === undefined) return;

      const durationMs = Math.round(performance.now() - startedAt);
      startTimes.delete(queryHash);

      if (durationMs < SLOW_QUERY_THRESHOLD_MS) return;

      const queryKey = query.queryKey;
      const slowQuery: SlowQueryEvent = {
        id: `${queryHash}-${Date.now()}`,
        queryKey,
        queryKeyLabel: toQueryKeyLabel(queryKey),
        entity: extractEntity(queryKey),
        operation: extractOperation(queryKey),
        layoutType: extractLayoutType(queryKey),
        durationMs,
        appId,
        observedAt: Date.now(),
      };

      setSlowQueries(prev => [slowQuery, ...prev].slice(0, MAX_SLOW_QUERIES));
      emitSentryBreadcrumb(slowQuery);
      void sendUsageTrackerEvent(slowQuery);
    });

    return () => unsubscribe();
  }, [appId, client]);

  const contextValue = useMemo<QueryPerformanceContextValue>(() => ({
    slowQueries,
    retryQuery: async (queryKey) => {
      await client.refetchQueries({ queryKey, exact: true, type: 'active' });
    },
  }), [client, slowQueries]);

  return (
    <QueryClientProvider client={client}>
      <QueryPerformanceContext.Provider value={contextValue}>
        {children}
      </QueryPerformanceContext.Provider>
    </QueryClientProvider>
  );
}

export function useQueryPerformance() {
  const context = useContext(QueryPerformanceContext);
  if (!context) {
    throw new Error('useQueryPerformance must be used within QueryPerformanceProvider');
  }

  return context;
}

export function SlowQueryIndicator() {
  const { slowQueries, retryQuery } = useQueryPerformance();

  if (!import.meta.env.DEV || slowQueries.length === 0) {
    return null;
  }

  return (
    <Card className="fixed bottom-4 right-4 z-50 w-96 border-border bg-card/95 p-3 shadow-sm backdrop-blur-sm">
      <Stack gap={2}>
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-warning" />
          <Typography variant="small" className="font-semibold">
            Slow queries (&gt; {SLOW_QUERY_THRESHOLD_MS}ms)
          </Typography>
        </div>
        {slowQueries.map(query => (
          <div key={query.id} className="rounded-md border border-border p-2">
            <Typography variant="small" className="font-medium break-all">{query.queryKeyLabel}</Typography>
            <div className="mt-1 flex items-center justify-between">
              <Typography variant="small" color="muted">{query.durationMs}ms</Typography>
              <Button size="sm" variant="secondary" onClick={() => void retryQuery(query.queryKey)}>Retry</Button>
            </div>
          </div>
        ))}
      </Stack>
    </Card>
  );
}
